import { FormContent, PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CaseWorkspace, { CaseFacts } from "@/components/case-workspace";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import { useResourceDraft } from "@/hooks/use-resource-draft";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  locationDetailingProductPath,
  locationDetailingProductsPath,
} from "@/routes";
import type { Product } from "./types";

export default function ProductForm({ product }: { product: Product | null }) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  const draft = useResourceDraft(
    {
      account: auth.account?.id || "",
      actor: auth.user?.id || "",
      location: auth.location?.id || "",
      kind: "product",
      record: product?.id || "new",
      revision: product?.revision || "new",
    },
    { name: product?.name || "", unit: product?.unit || "litre" },
  );
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(product ? "inventory.edit" : "inventory.add");
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(locationId),
        },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <CaseWorkspace
          context={
            <CaseFacts
              facts={[
                {
                  label: t("resource_workspace.location"),
                  value: auth.location.name,
                },
                ...(product
                  ? [
                      { label: t("inventory.name"), value: product.name },
                      {
                        label: t("inventory.balance"),
                        value: `${product.quantity} ${t(`inventory.${product.unit}`)}`,
                      },
                    ]
                  : []),
              ]}
            />
          }
        >
          <FormContent>
            <h1 className="text-page font-display">{title}</h1>
            <FormErrors
              errors={errors || {}}
              targets={{ name: "product-name", unit: "product-unit" }}
            />
            {draft.status && (
              <p role="status" className="text-sm text-muted-foreground">
                {t(`resource_drafts.${draft.status}`)}
              </p>
            )}
            <Form
              key={product?.revision || "new"}
              onSuccess={draft.discard}
              action={
                product
                  ? locationDetailingProductPath(locationId, product.id)
                  : locationDetailingProductsPath(locationId)
              }
              method={product ? "patch" : "post"}
            >
              {({ processing }) => (
                <div className="space-y-4">
                  {product && (
                    <input
                      type="hidden"
                      name="revision"
                      value={product.revision}
                    />
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="product-name">{t("inventory.name")}</Label>
                    <Input
                      autoFocus
                      required
                      id="product-name"
                      name="detailing_product[name]"
                      value={draft.values.name}
                      onChange={(event) =>
                        draft.change("name", event.target.value)
                      }
                      maxLength={200}
                      aria-invalid={!!errors?.name}
                      aria-describedby={
                        errors?.name ? "product-name-error" : undefined
                      }
                    />
                    {errors?.name && (
                      <p
                        id="product-name-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.name)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="product-unit">{t("inventory.unit")}</Label>
                    {product ? (
                      <Input
                        id="product-unit"
                        readOnly
                        value={t(`inventory.${product.unit}`)}
                        aria-describedby="unit-help"
                      />
                    ) : (
                      <Select
                        name="detailing_product[unit]"
                        value={
                          draft.values.unit === "piece" ? "piece" : "litre"
                        }
                        onValueChange={(value) => {
                          if (value) draft.change("unit", value);
                        }}
                        items={{
                          litre: t("inventory.litre"),
                          piece: t("inventory.piece"),
                        }}
                      >
                        <SelectTrigger
                          id="product-unit"
                          className="w-full"
                          aria-describedby={
                            errors?.unit
                              ? "unit-help product-unit-error"
                              : "unit-help"
                          }
                          aria-invalid={!!errors?.unit}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="litre">
                            {t("inventory.litre")}
                          </SelectItem>
                          <SelectItem value="piece">
                            {t("inventory.piece")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {errors?.unit && (
                      <p
                        id="product-unit-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.unit)}
                      </p>
                    )}
                    <p id="unit-help" className="text-muted-foreground text-sm">
                      {t("inventory.unit_locked")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("inventory.save")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={draft.discard}
                      render={
                        <Link
                          href={
                            product
                              ? locationDetailingProductPath(
                                  locationId,
                                  product.id,
                                )
                              : locationDetailingProductsPath(locationId)
                          }
                        />
                      }
                    >
                      {t("inventory.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </FormContent>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
